import { QuestionsUniqueKeys } from 'src/containers/QuestionnaireParent/QuestionnaireEnum';
import { GetUserTimeZone } from './timeZone';
import { updateCombinedFilters } from 'src/redux/actions/questionnaireActions';

interface PageQuestionChoice {
  id?: string;
  unique_key: string;
  title?: string;
  selected?: boolean;
  value?: any;
  raw_metadata?: string;
  metadata?: Record<string, any>;
  [key: string]: any;
}

interface PageQuestion {
  id: string;
  unique_key: string;
  choices?: PageQuestionChoice[];
  [key: string]: any;
}

interface CombinedFilters {
  filters: Array<{
    sequence?: number;
    filter_key: string;
    filter_label?: string;
    filter_sub_header?: string;
    filter_component_type?: string;
    filter_group?: string;
    locked?: boolean;
    skip_for_self_match?: boolean;
    is_grouped?: boolean;
    filter_selection_data: Array<{
      label?: string;
      unique_key?: string;
      selected?: boolean;
      value?: any;
      metadata?: string;
      group?: any[];
    }>;
  }>;
  schedule_request: {
    start_date?: string;
    callAPI?: boolean;
    time_from?: any;
    time_until?: any;
    schedule_day?: any[];
    schedules_with_slots?: boolean;
    for_days?: number;
    user_time_zone?: string;
    sort_by?: string;
    meeting_pref?: string[];
    service_type?: string;
    [key: string]: any;
  };
  therapist_id: string;
  is_clinician_availability_page: boolean;
  [key: string]: any;
}

export const searchPayload = (
  modalQuestions: PageQuestion[],
  combinedFilters: CombinedFilters,
  clientId: string,
  startDateAndTime: string,
) => {
  const payload = JSON.parse(JSON.stringify(combinedFilters));
  const userTimeZone = GetUserTimeZone();

  const serviceQuestion = modalQuestions.find(
    q => q.unique_key === QuestionsUniqueKeys?.providerServicesClientQuestion,
  );
  const insuranceQuestion = modalQuestions.find(
    q => q.unique_key === QuestionsUniqueKeys?.insuranceClientQuestion,
  );
  const ageQuestion = modalQuestions.find(
    q =>
      q.unique_key ===
      QuestionsUniqueKeys?.patientsAgeSelfMatchingClientQuestionV1,
  );
  const locationQuestion = modalQuestions.find(
    q => q.unique_key === QuestionsUniqueKeys?.patientLocation,
  );

  const selectedService = serviceQuestion?.choices?.find(c => c.selected);
  const selectedInsurance = insuranceQuestion?.choices?.find(c => c.selected);
  const selectedAge = ageQuestion?.choices?.find(c => c.selected);
  const locationChoice = locationQuestion?.choices?.[0];

  // Update Combined filters with selected modal question choices values
  if (payload.filters && Array.isArray(payload.filters)) {
    // Update Client Location
    const locationFilterIndex = payload.filters.findIndex(
      (f: any) => f.filter_key === 'clientLocation',
    );

    if (locationFilterIndex !== -1 && locationChoice) {
      payload.filters[locationFilterIndex].filter_selection_data[0] = {
        label: 'Your Location',
        unique_key: locationQuestion.unique_key,
        value: locationChoice.value || '',
      };
    }

    // Update Insurance Plan
    const insuranceFilterIndex = payload.filters.findIndex(
      (f: any) => f.filter_key === 'insurancePlan',
    );

    if (insuranceFilterIndex !== -1 && selectedInsurance) {
      payload.filters[insuranceFilterIndex].filter_selection_data[0] = {
        label: selectedInsurance.title || selectedInsurance.place_holder || '',
        unique_key: selectedInsurance.unique_key,
        selected: true,
        group: [],
      };

      // Include metadata if available
      if (selectedInsurance.raw_metadata) {
        payload.filters[
          insuranceFilterIndex
        ].filter_selection_data[0].metadata = selectedInsurance.raw_metadata;
      }
    }

    // Update Age Group
    const ageFilterIndex = payload.filters.findIndex(
      (f: any) => f.filter_key === 'ageGroup',
    );

    if (ageFilterIndex !== -1 && selectedAge) {
      payload.filters[ageFilterIndex].filter_selection_data[0] = {
        label: selectedAge.title || selectedAge.place_holder || '',
        unique_key: selectedAge.unique_key,
        selected: true,
        metadata: selectedAge.raw_metadata || '',
        group: [],
      };
    }
  }

  // Update schedule_request.service_type with selected service unique key
  if (payload.schedule_request && selectedService) {
    payload.schedule_request.service_type = selectedService.unique_key;
  }

  // Update schedule_request.start_date with selected start date and time
  payload.schedule_request.start_date = startDateAndTime;

  // Update schedule_request.user_time_zone with user's time zone
  payload.schedule_request.user_time_zone = userTimeZone;

  // update the client_id
  payload.client_id = clientId;

  return payload;
};

export const syncModalSelectionsToCombinedFilters = (
  modalQuestions: PageQuestion[],
  currentCombinedFilters: CombinedFilters,
  dispatch: any,
) => {
  const updatedFilters = JSON.parse(JSON.stringify(currentCombinedFilters));

  // Extract selected values from modalQuestions
  const selectedService = modalQuestions
    .find(
      q => q.unique_key === QuestionsUniqueKeys.providerServicesClientQuestion,
    )
    ?.choices?.find(c => c.selected);
  const selectedInsurance = modalQuestions
    .find(q => q.unique_key === QuestionsUniqueKeys.insuranceClientQuestion)
    ?.choices?.find(c => c.selected);
  const selectedAge = modalQuestions
    .find(
      q =>
        q.unique_key ===
        QuestionsUniqueKeys.patientsAgeSelfMatchingClientQuestionV1,
    )
    ?.choices?.find(c => c.selected);
  const locationChoice = modalQuestions.find(
    q => q.unique_key === QuestionsUniqueKeys.patientLocation,
  )?.choices?.[0];

  // Update each filter
  if (updatedFilters.filters) {
    const locationFilter = updatedFilters.filters.find(
      (f: any) => f.filter_key === 'clientLocation',
    );

    if (locationFilter && locationChoice) {
      locationFilter.filter_selection_data[0] = {
        label: 'Your Location',
        unique_key: QuestionsUniqueKeys.patientLocation,
        value: locationChoice.value || '',
      };
    }

    const insuranceFilter = updatedFilters.filters.find(
      (f: any) => f.filter_key === 'insurancePlan',
    );

    if (insuranceFilter && selectedInsurance) {
      insuranceFilter.filter_selection_data[0] = {
        label: selectedInsurance.title || selectedInsurance.place_holder || '',
        unique_key: selectedInsurance.unique_key,
        selected: true,
        group: [],
        metadata: selectedInsurance.raw_metadata,
      };
    }

    const ageFilter = updatedFilters.filters.find(
      (f: any) => f.filter_key === 'ageGroup',
    );

    if (ageFilter && selectedAge) {
      ageFilter.filter_selection_data[0] = {
        label: selectedAge.title || selectedAge.place_holder || '',
        unique_key: selectedAge.unique_key,
        selected: true,
        metadata: selectedAge.raw_metadata || '',
        group: [],
      };
    }
  }

  if (updatedFilters.schedule_request && selectedService) {
    updatedFilters.schedule_request.service_type = selectedService.unique_key;
  }

  if (dispatch) {
    dispatch(updateCombinedFilters(updatedFilters));
  }

  return updatedFilters;
};
